import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  CircularProgress,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import useStore from "../helpers/useStore";
import { TransactionsContext } from "../stores/Transactions/Transactions.provider";
import { LineItemDto } from "../api-client";

interface LineItemViewerProps {
  rxId: string;
  autoload?: boolean; // optional, defaults to false
}

interface AggregatedLineItem extends LineItemDto {
  count: number;
}

const aggregate = (items: LineItemDto[]): AggregatedLineItem[] => {
  const aggregated: Record<string, AggregatedLineItem> = {};

  // console.log("Lineitems", items);

  items.forEach((item) => {
    const key = item.priceId;
    if (!aggregated[key]) {
      aggregated[key] = { ...item, count: 1 };
    } else {
      aggregated[key].count += 1;
      aggregated[key].copayment += item.copayment;
    }
  });

  return Object.values(aggregated);
};

const LineItemViewer: React.FC<LineItemViewerProps> = ({ rxId, autoload = false }) => {
  const [lineItems, setLineItems] = useState<AggregatedLineItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // Distinguishes "nothing fetched yet" from "fetched and there is nothing",
  // so the empty state only speaks once it has something to say.
  const [loaded, setLoaded] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const TransactionStore = useStore(TransactionsContext);

  const loadLineItems = async () => {
    setLoading(true);
    setFailed(false);
    try {
      if (rxId) {
        const data = await TransactionStore.api.getPriceDataForRx(rxId);
        const items: LineItemDto[] = data.lineItems;
        setLineItems(aggregate(items));
        setLoaded(true);
      }
    } catch (error) {
      console.error("Failed to load line items:", error);
      // Reported rather than swallowed: a failed load left showing "keine
      // Positionen" would read as a billing fact rather than a broken request.
      setFailed(true);
      setLineItems([]);
    }
    setLoading(false);
  };

  // If autoload is true, fetch on mount (or whenever rxId changes)
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    if (autoload) {
      loadLineItems();
    } else {
      setLineItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoload, rxId]);

  const formatDate = (d: Date) =>
    d.toLocaleDateString("DE", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    });

  return (
    <Box>
      {!autoload && (
        <Button
          variant="contained"
          color="primary"
          onClick={loadLineItems}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : "laden"}
        </Button>
      )}

      {loading && (
        <Typography variant="body2" color="textSecondary">
          Positionen werden geladen …
        </Typography>
      )}

      {!loading && failed && (
        <Typography variant="body2" color="error">
          Abrechnungspositionen konnten nicht geladen werden.
        </Typography>
      )}

      {/* An explicit empty state: without it a prescription that genuinely has
          no positions is indistinguishable from one still loading. */}
      {!loading && loaded && !failed && lineItems.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          Keine Abrechnungspositionen vorhanden.
        </Typography>
      )}

      {!loading && lineItems.length > 0 && (
        <Table>
          <TableBody>
            {lineItems.map((item) => (
              <TableRow key={item.priceId} >
                <TableCell align="right">{item.count}</TableCell>
                <TableCell title={item.description}>{item.abbreviation}</TableCell>
                <TableCell>{item.code}</TableCell>
                <TableCell title={item.priceId} align="right">{Number(item.price).toFixed(2)}</TableCell>
                <TableCell align="right">{Number(item.copayment).toFixed(2)}</TableCell>
                <TableCell>{formatDate(new Date(item.date))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default LineItemViewer;
